import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Icon, type IconName } from '@/icons';
import { LandingHero } from './LandingHero';
import landscape from '../assets/hero-landscape-v1.png';

const Stage = styled.div`
  position: relative;
  isolation: isolate;
  padding: 20px 26px 36px;
  background-color: ${({ theme }) => theme.color.bgRoot};
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    pointer-events: none;
    background:
      linear-gradient(to bottom, transparent 58%, ${({ theme }) => theme.color.bgRoot}),
      url(${landscape}) center bottom / cover no-repeat;
    opacity: ${({ theme }) => (theme.mode === 'dark' ? 1 : 0.12)};
  }
  @media (max-width: 767.98px) {
    padding: 0 20px 28px;
  }
`;
const Columns = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  gap: 28px;
  max-width: 1540px;
  margin: auto;
  align-items: center;
  @media (max-width: 1340px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 32px;
  }
`;
const Side = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
`;
const Eyebrow = styled.h2`
  margin: 0 0 10px;
  display: flex;
  align-items: center;
  gap: 20px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: 1.6;
  font-weight: 500;
  color: ${({ theme }) => theme.color.accentCyan};
  &::after {
    content: '';
    height: 1px;
    flex: 1;
    background: currentColor;
    opacity: 0.45;
  }
`;
const Features = styled.div`
  display: grid;
  gap: 14px;
  @media (min-width: 768px) and (max-width: 1340px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;
const Feature = styled(Link)`
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr) 12px;
  gap: 14px;
  align-items: center;
  padding: 16px;
  min-height: 104px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.color.surface}ED;
  text-decoration: none;
  color: ${({ theme }) => theme.color.textPrimary};
  transition:
    border-color 160ms ease,
    background 160ms ease;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 3px;
  }
  strong {
    display: block;
    font-size: ${({ theme }) => theme.fontSize.sm};
    font-weight: 650;
    margin-bottom: 6px;
  }
  p {
    margin: 0;
    font-size: ${({ theme }) => theme.fontSize.sm};
    line-height: 1.5;
    color: ${({ theme }) => theme.color.textMuted};
  }
`;
const Mark = styled.span`
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  color: ${({ theme }) => theme.color.accentCyan};
  background: ${({ theme }) => theme.color.accentCyan}17;
`;
const Horizon = styled.div`
  height: 104px;
  position: relative;
  overflow: hidden;
  padding-top: 14px;
  color: ${({ theme }) => theme.color.accentCyan};
  p {
    margin: 0;
    max-width: 24ch;
    font-size: ${({ theme }) => theme.fontSize.xs};
    line-height: 1.9;
    position: relative;
    z-index: 1;
  }
  &::after {
    content: '';
    position: absolute;
    width: 290px;
    height: 290px;
    left: 90px;
    top: 58px;
    border-radius: 50%;
    box-shadow: -5px -7px 20px ${({ theme }) => theme.color.accentCyan}70;
    background:
      radial-gradient(
        circle at 25% 8%,
        ${({ theme }) => theme.color.accentCyan}B0,
        transparent 55%
      ),
      url(${landscape}) center / cover;
  }
`;
const Values = styled.div`
  max-width: 1540px;
  margin: 20px auto 0;
  padding: 24px 16px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 20px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.color.surface}ED;
  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 520px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const Value = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
  padding: 0 8px;
  & > span {
    color: ${({ theme }) => theme.color.accentCyan};
    flex-shrink: 0;
  }
  strong {
    display: block;
    font-size: ${({ theme }) => theme.fontSize.sm};
    font-weight: 600;
  }
  p {
    margin: 5px 0 0;
    font-size: ${({ theme }) => theme.fontSize.xs};
    line-height: 1.5;
    color: ${({ theme }) => theme.color.textMuted};
  }
`;
const features: { title: string; body: string; icon: IconName; to: string }[] = [
  {
    title: 'Transparent moderation',
    body: 'Real people. Real reasons. All in public.',
    icon: 'eye',
    to: '/council',
  },
  {
    title: 'Public verdicts',
    body: 'Decisions made by a randomly selected jury of members.',
    icon: 'file-text',
    to: '/council',
  },
  {
    title: 'A real community',
    body: 'Different perspectives. Bigger conversations.',
    icon: 'users',
    to: '/feed',
  },
  {
    title: 'Built for free speech',
    body: 'Five clear rules. Nothing else. More speech. Less silence.',
    icon: 'shield',
    to: '/policies/guidelines',
  },
];
const values: { title: string; body: string; icon: IconName }[] = [
  { title: 'Open discussions', body: 'Different ideas. Real people.', icon: 'compass' },
  { title: 'Global community', body: 'Voices from around the world.', icon: 'globe' },
  { title: 'More transparency', body: 'See how decisions are made.', icon: 'bar-chart-3' },
  { title: 'A healthier internet', body: 'Built by people, for people.', icon: 'heart' },
];
export function LandingShowcase() {
  return (
    <Stage data-testid="landing-showcase">
      <Columns>
        <LandingHero />
        <Side aria-labelledby="landing-features-title">
          <Eyebrow id="landing-features-title">A more open internet</Eyebrow>
          <Features>
            {features.map((item) => (
              <Feature key={item.title} to={item.to}>
                <Mark>
                  <Icon name={item.icon} size={24} />
                </Mark>
                <div>
                  <strong>{item.title}</strong>
                  <p>{item.body}</p>
                </div>
                <Icon name="chevron-right" size={16} />
              </Feature>
            ))}
          </Features>
          <Horizon aria-hidden="true">
            <p>A brighter conversation awaits</p>
          </Horizon>
        </Side>
      </Columns>
      <Values aria-label="What TruXSocial stands for">
        {values.map((item) => (
          <Value key={item.title}>
            <Icon name={item.icon} size={28} />
            <div>
              <strong>{item.title}</strong>
              <p>{item.body}</p>
            </div>
          </Value>
        ))}
      </Values>
    </Stage>
  );
}
